class SwiperSlider extends HTMLElement {
  constructor() {
    super();
    this.slideContainer = this.querySelector('[data-swiper]:not([data-swiper-thumbs])');
    this.slides = this.querySelectorAll('[data-swiper-slide]');
    this.sliderOptions = this.parseOptions(this.dataset.options);
    this.activeIndex = 0;
    this.initialized = false;
    this.updateFormOnSlideChange = this.hasAttribute('data-update-form-on-change') || false;
    this.relatedForm = document.querySelector(this.getAttribute('data-related-form')) || null;
    this.thumbnails = this.querySelector('[data-swiper-thumbs]');

    if (!this.hasAttribute('disable-auto-init')) {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    if (this.slideContainer && this.slides.length > 0) {
      document.addEventListener('DOMContentLoaded', () => this.initializeSwiper());
      document.addEventListener('shopify:section:load', () => this.initializeSwiper());
      window.addEventListener(
        'resize',
        this.debouncedResize(() => this.updateSliderSize(), 300)
      );
    } else {
      console.warn('SwiperSlider - Slider or Slides Not Found');
    }
  }

  goToSlide(index) {
    if (!this.slider || index === undefined) return;
    this.slider.progressLoop ? this.slider.slideToLoop(index) : this.slider.slideTo(index);
  }

  getSlide(index) {
    if (!this.slider || index === undefined) return;
    return this.slides[index];
  }

  parseOptions(options) {
    try {
      if (options) {
        const sanitizedOptions = options.replace(/'/g, '"');
        return JSON.parse(sanitizedOptions || '{}');
      }
      return {};
    } catch (error) {
      console.error('Error parsing options: ', error);
      return {};
    }
  }

  initializeSwiper() {
    if (window.Swiper && !this.initialized) {
      this.initialized = true;
      this.setThumbnails();
      this.slider = new Swiper(this.slideContainer, this.sliderOptions);

      this.slider.on('slideChange', (e) => {
        this.updateSlideAttributes();

        if (this.updateFormOnSlideChange) {
          this.updateRelatedForm(this.getSlide(this.slider.realIndex));
        }

        if (this.hasAttribute('data-update-property-node')) {
          const node = document.querySelector(this.getAttribute('data-update-property-node'));
          const propertyName = this.getSlide(this.slider.realIndex).getAttribute('data-update-property-name');
          const propertyValue = this.getSlide(this.slider.realIndex).getAttribute('data-update-property-value');
          this.updateCustomProperty(node, propertyName, propertyValue);
        }
      });

      this.slider.on('reachEnd', () => this.setAttribute('data-reached-end', ''));
      this.slider.on('reachBeginning', () => this.setAttribute('data-reached-start', ''));
      this.slider.on('fromEdge', () => {
        this.removeAttribute('data-reached-end');
        this.removeAttribute('data-reached-start');
      });

      // this.handleAutoplay();
      this.updateSlideAttributes();
      this.observeSlideChanges();
    } else if (!window.Swiper) {
      console.warn('SwiperSlider - Swiper.js not found');
    }
  }

  updateCustomProperty(targetContainer, propertyName, propertyValue) {
    if (!targetContainer || !propertyName || propertyValue == null) return false;
    targetContainer.style.setProperty(`--${propertyName}`, propertyValue);
    return true;
  }

  updateSliderSize() {
    this.slider?.updateSize();
  }

  updateSlideAttributes() {
    if (!this.slider) return;

    if (this.slider.isBeginning) {
      this.setAttribute('data-reached-start', '');
    } else {
      this.removeAttribute('data-reached-start');
    }

    if (this.slider.isEnd) {
      this.setAttribute('data-reached-end', '');
    } else {
      this.removeAttribute('data-reached-end');
    }
  }

  initializeSliderEventListeners() {
    if (this.slider) {
      this.slider.on('slideChange', () => {
        this.updateSlideAttributes();
      });
    }
  }

  updateRelatedForm(slide) {
    if (!this.relatedForm || !slide) return;

    const productData = JSON.parse(slide.querySelector('[data-swiper-product-info]').textContent);
    const updateFields = {
      'input[name="product-id"]': productData.id,
      '[data-form-update="variant_id"]': productData.variantId,
      '[data-form-update="title"]': productData.title,
      '[data-form-update="shortDescription"]': productData.shortDescription,
      '[data-form-update="price"]': productData.price,
      '[data-form-update="url"]': productData.url
    };

    Object.entries(updateFields).forEach(([selector, value]) => {
      const element = this.relatedForm.querySelector(selector);
      if (element) {
        if (element.tagName === 'INPUT' || element.tagName === 'OPTION') {
          element.value = value;
        } else if (element.tagName === 'A') {
          element.href = value;
        } else {
          element.textContent = value;
        }
      }
    });
  }

  setThumbnails() {
    if (this.thumbnails) {
      const thumbOptions = this.parseOptions(this.thumbnails.dataset.swiperOptions);
      const thumbnailsSlider = new Swiper(this.thumbnails, thumbOptions);
      this.sliderOptions.thumbs = { swiper: thumbnailsSlider };
    }
  }

  debouncedResize(callback, delay) {
    let timeout;
    return () => {
      clearTimeout(timeout);
      timeout = setTimeout(callback, delay);
    };
  }

  observeSlideChanges() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && ['open', 'active', 'selected'].includes(mutation.attributeName)) {
          if (mutation.oldValue === null) {
            const slide = mutation.target.closest('[data-swiper-slide]');
            if (slide) {
              const index = Array.from(this.slides).indexOf(slide);
              if (index !== -1) {
                this.goToSlide(index);
              }
            }
          }
        }
      });
    });

    this.slides.forEach((slide) => {
      observer.observe(slide, {
        attributes: true,
        subtree: true,
        attributeFilter: ['open', 'active', 'selected'],
        attributeOldValue: true,
      });
    });
  }
}

customElements.define('swiper-slider', SwiperSlider);
